import React, { useState } from 'react';
import { FileText, X } from 'lucide-react';

export default function ResellerAgreement({
  onAccept,
  onClose,
  resellerPrice,
  weeklyPaymentDate,
  companyName,
  viewOnly = false
}) {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const handleScroll = (e) => {
    const element = e.target;
    const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    if (isAtBottom && !scrolledToBottom) {
      setScrolledToBottom(true);
    }
  };

  const handleAccept = () => {
    if (agreed) {
      onAccept();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {viewOnly ? 'Reseller Agreement' : 'Reseller Partnership Agreement'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {companyName || 'Merch Masters LK'}
              </p>
            </div>
          </div>
          {viewOnly && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Scrollable Agreement Content */}
        <div
          className="flex-1 overflow-y-auto p-6 space-y-4 text-gray-700 dark:text-gray-300"
          onScroll={handleScroll}
        >
          {/* Agreement Header */}
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              RESELLER AGREEMENT
            </h3>
            <div className="text-sm space-y-1">
              <p><strong>Supplier:</strong> Merch Masters LK</p>
              <p><strong>Product:</strong> 3D Night Lamps</p>
              <p><strong>Reseller:</strong> {companyName || '__________________________'}</p>
              <p><strong>Agreed Price:</strong> <span className="text-blue-600 dark:text-blue-400 font-bold">
                LKR {resellerPrice?.toLocaleString() || '__________________________'}</span></p>
              <p><strong>Date:</strong> {new Date().toLocaleDateString('en-GB')}</p>
            </div>
          </div>

          {/* Section 1: Purpose */}
          <section>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              1. Purpose of This Agreement
            </h4>
            <div className="text-sm leading-relaxed">
              <p>
                The Reseller is authorized to find customers and sell 3D Night Lamps produced by Merch Masters LK ("Supplier").
                The Supplier will manufacture the lamps based on the confirmed designs submitted by the Reseller.
              </p>
            </div>
          </section>

          {/* Section 2: Pricing */}
          <section>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              2. Pricing
            </h4>
            <div className="space-y-2 text-sm leading-relaxed">
              <p>
                • <strong>Reseller Price:</strong> <span className="text-blue-600 dark:text-blue-400 font-bold">
                LKR {resellerPrice?.toLocaleString() || '3,890'}</span> per lamp.
              </p>
              <p>
                • The Reseller may sell the lamps at any price they choose.
              </p>
              <p>
                • <strong>Reseller Profit</strong> = Selling Price – LKR {resellerPrice?.toLocaleString() || '3,890'}
              </p>
            </div>
          </section>

          {/* Section 3: Order Process */}
          <section>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              3. Order Process
            </h4>
            <div className="space-y-2 text-sm leading-relaxed">
              <p>
                The Supplier will provide the Reseller with a login to: <a href="https://merchm.store/login" className="text-blue-600 dark:text-blue-400 underline" target="_blank" rel="noopener noreferrer">https://merchm.store/login</a>
              </p>
              <p><strong>Reseller:</strong></p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Collects the customer's photo(s), text, and necessary details.</li>
                <li>Creates the lamp design using the website.</li>
                <li>Shows the design to the customer and obtain approval.</li>
                <li>Place the final order through the website.</li>
              </ul>
              <p>
                Once the order is submitted, the Supplier will begin production.
              </p>
            </div>
          </section>

          {/* Section 4: Delivery Options */}
          <section>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              4. Delivery Options
            </h4>
            <div className="space-y-2 text-sm leading-relaxed">
              <p>At the time of order placement, the Reseller may choose:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Delivery to the Reseller's shop for pickup, or</li>
                <li>Direct delivery to the customer.</li>
              </ul>
              <p>• All deliveries are free of charge (0 LKR).</p>
              <p>• Standard delivery lead time is 2 days from the date the Supplier hands over the product to the delivery service.</p>
            </div>
          </section>

          {/* Section 5: Payment Terms */}
          <section>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              5. Payment Terms
            </h4>
            <div className="space-y-2 text-sm leading-relaxed">
              <p>
                At the end of each week (<strong className="text-blue-600 dark:text-blue-400">{weeklyPaymentDate || 'Friday, Saturday, Sunday'}</strong>), the Reseller will:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Review the total number of lamps ordered.</li>
                <li>Pay LKR {resellerPrice?.toLocaleString() || '3,890'} × total lamps to the Supplier.</li>
              </ul>
              <p className="font-semibold text-orange-600 dark:text-orange-400">
                If payments are delayed, the Supplier may pause the reseller account and hold all ongoing orders until full payment is received.
              </p>
            </div>
          </section>

          {/* Section 6: Sample Lamps */}
          <section>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              6. Sample Lamps
            </h4>
            <div className="text-sm leading-relaxed">
              <p>
                The Supplier will provide two sample lamps for display and promotional use. These items are returnable and cannot be sold.
              </p>
            </div>
          </section>

          {/* Section 7: Responsibilities */}
          <section>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              7. Responsibilities of Each Party
            </h4>
            <div className="space-y-3 text-sm leading-relaxed">
              <div>
                <p className="font-semibold">Supplier:</p>
                <ul className="list-disc pl-6 space-y-1 mt-1">
                  <li>Produce the lamp according to the final approved design submitted by the Reseller.</li>
                  <li>Dispatch the product to delivery on time.</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold">Reseller:</p>
                <ul className="list-disc pl-6 space-y-1 mt-1">
                  <li>Manage customer inquiries.</li>
                  <li>Ensure all photos, texts, and details submitted are accurate and legally allowed (No illegal or harmful content).</li>
                  <li className="font-semibold text-orange-600 dark:text-orange-400">
                    No changes are allowed after submitting the customer's final details and design. Reseller should accept full responsibility for the accuracy of all information submitted.
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 8: Brand Usage */}
          <section>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              8. Brand Usage
            </h4>
            <div className="space-y-2 text-sm leading-relaxed">
              <p>
                "Merch Masters LK" is the sole property of the Supplier.
              </p>
              <p>
                The Reseller may use the phrase: <strong>"Official Reseller of Merch Masters LK 3D Night Lamps."</strong>
              </p>
              <p className="font-semibold text-orange-600 dark:text-orange-400">
                The Reseller may not claim ownership of the brand or imply that they manufacture the products.
              </p>
            </div>
          </section>

          {/* Section 9: Termination */}
          <section>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              9. Termination of Agreement
            </h4>
            <div className="space-y-2 text-sm leading-relaxed">
              <p>
                Either party may end this agreement with one week's written or WhatsApp notice.
              </p>
              <p className="font-semibold text-orange-600 dark:text-orange-400">
                All outstanding payments must be settled before the termination date.
              </p>
            </div>
          </section>

          {/* Acknowledgment */}
          <section className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-300 mb-2">
              Acknowledgment
            </h4>
            <p className="text-sm text-blue-800 dark:text-blue-400">
              By accepting this agreement, you acknowledge that you have read, understood, and agree to be
              bound by all terms and conditions outlined above. This is a legally binding agreement between
              you ({companyName || 'Reseller'}) and Merch Masters LK.
            </p>
          </section>

          {/* Scroll Indicator */}
          {!viewOnly && !scrolledToBottom && (
            <div className="sticky bottom-0 text-center py-4 bg-gradient-to-t from-white dark:from-gray-800 to-transparent">
              <p className="text-sm text-gray-500 dark:text-gray-400 animate-bounce">
                ↓ Please scroll to the bottom to continue
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {!viewOnly && (
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 space-y-4">
            {/* Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                disabled={!scrolledToBottom}
                className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <span className={`text-sm ${scrolledToBottom ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400'}`}>
                I have read and agree to all terms and conditions of this Reseller Agreement.
                I understand that this is a legally binding agreement.
              </span>
            </label>

            {/* Buttons */}
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Are you sure you want to decline? You will be signed out.')) {
                    onClose();
                  }
                }}
                className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 font-medium transition-colors"
              >
                Decline
              </button>
              <button
                type="button"
                onClick={handleAccept}
                disabled={!agreed || !scrolledToBottom}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                Accept Agreement
              </button>
            </div>
          </div>
        )}

        {/* View Only Footer */}
        {viewOnly && (
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-center">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
